import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { z } from "zod";
import { authoringCreatedBySchema, authoringSlugSchema, authoringTenantSlugSchema } from "./authoring-shared.js";
import { twinAuthoringArtifactKindSchema, twinAuthoringArtifactRoleSchema, twinAuthoringRunRecordSchema, twinAuthoringRunRequestV1Schema, } from "./twin-authoring.js";
import { canonicalTwinBoundaryJson } from "./twin-logic.js";
const boundedText = (maximum) => z.string().trim().min(1).max(maximum);
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const positiveTimeSchema = z.number().int().positive().safe();
const organisationIdSchema = boundedText(200);
const relationSchema = z.string().regex(/^connector_warehouse\.[a-z][a-z0-9_]{0,126}$/u);
const preparationIdSchema = z.string().regex(/^ta_prep_[0-9a-f]{32}$/u);
const preparationProfileIdSchema = z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,127}$/u);
function isSortedUnique(values) {
    return new Set(values).size === values.length
        && values.every((value, index) => index === 0 || values[index - 1] < value);
}
export const twinAuthoringPreparationSourceSelectionSchema = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("all_current") }).strict(),
    z.object({
        mode: z.literal("selected"),
        relations: z.array(relationSchema).min(1).max(256),
    }).strict().refine(({ relations }) => isSortedUnique(relations), {
        path: ["relations"],
        message: "Selected source relations must be unique and sorted.",
    }),
]);
const authoringModelSchema = z.enum(["gpt-6-astra", "gemini-3.8-flash"]);
export const twinAuthoringModelSelectionSchema = z.object({
    author: authoringModelSchema.optional(), evaluator: authoringModelSchema.optional(),
    authorReasoning: z.enum(["low", "medium", "high"]).optional(),
    evaluatorReasoning: z.enum(["low", "medium", "high"]).optional(),
}).strict().refine((value) => value.author || value.evaluator || value.authorReasoning || value.evaluatorReasoning, "Select at least one model or reasoning level.");
/**
 * The routing terms (aliases and activation phrases, in activation's normal form) each live product
 * claims when a build starts. Activation refuses a product that shares a term with a live product it
 * does not supersede, so a build gives its products distinct terms or names the product it replaces.
 */
export const twinAuthoringLiveRoutingCatalogueV1Schema = z.object({
    schemaVersion: z.literal("1.0.0"),
    products: z.array(z.object({
        productSlug: authoringSlugSchema,
        description: boundedText(4_000),
        routingTerms: z.array(z.string().min(1).max(240)).max(96),
    }).strict().refine(({ routingTerms }) => isSortedUnique(routingTerms)
        && routingTerms.every((term) => term === term.trim().toLowerCase().replace(/\s+/gu, " ")), {
        path: ["routingTerms"],
        message: "Routing terms must be normalised, unique and sorted.",
    })).max(1_000),
}).strict().refine(({ products }) => isSortedUnique(products.map(({ productSlug }) => productSlug)), {
    path: ["products"],
    message: "Live products must be unique and sorted by slug.",
});
/**
 * The live product versions a full rebuild versions its repeated products against, each with the
 * semantic authoring record of the release it came from. The worker reads their plans from those
 * records; only the compiler uses them.
 */
export const twinAuthoringLiveProductsV1Schema = z.object({
    schemaVersion: z.literal("1.0.0"),
    products: z.array(z.object({
        productSlug: authoringSlugSchema,
        productVersion: z.number().int().positive(),
        releaseId: z.string().regex(/^ta_rel_[0-9a-f]{32}$/u),
        semanticAuthoringRecordSha256: sha256Schema,
    }).strict()).max(1_000),
}).strict().refine(({ products }) => isSortedUnique(products.map(({ productSlug }) => productSlug)), {
    path: ["products"],
    message: "Live products must be unique and sorted by slug.",
}).refine(({ products }) => products.every((product) => products.every((other) => other.releaseId !== product.releaseId
    || other.semanticAuthoringRecordSha256 === product.semanticAuthoringRecordSha256)), {
    path: ["products"],
    message: "A release has one semantic authoring record.",
});
const twinAuthoringPreparationRequestFields = {
    modelSelection: twinAuthoringModelSelectionSchema.optional(),
    liveRoutingCatalogue: twinAuthoringLiveRoutingCatalogueV1Schema.optional(),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    target: boundedText(120),
    idempotencyKey: boundedText(200),
    warehouseOrganisationIds: z.array(organisationIdSchema).length(1),
    requestedBy: authoringCreatedBySchema,
    profileId: preparationProfileIdSchema,
    sourceSelection: twinAuthoringPreparationSourceSelectionSchema,
};
const warehouseScopeIsSorted = ({ warehouseOrganisationIds }) => isSortedUnique(warehouseOrganisationIds);
const catalogueEntryNameSchema = z.string().regex(/^[a-z][a-z0-9_-]{0,62}\/[a-z][a-z0-9_-]{0,126}$/u);
/**
 * Connector catalogue entries a build applies. The build worker assembles their plan from its own
 * catalogue for the organisation's relations; the plan authors the run, so no model is called.
 */
const productSlugSchema = z.string().max(80).regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u);
export const twinAuthoringCatalogueSelectionSchema = z.object({
    connector: z.string().regex(/^[a-z][a-z0-9_-]{0,62}$/u),
    entries: z.array(catalogueEntryNameSchema).min(1).max(64).refine(isSortedUnique, "Catalogue entries must be unique and sorted."),
    // Live products a selected entry's public product replaces here, chosen by a person.
    supersede: z.array(z.object({
        entry: catalogueEntryNameSchema,
        productSlugs: z.array(productSlugSchema).min(1).max(16).refine(isSortedUnique, "Superseded product slugs must be unique and sorted."),
    }).strict()).max(64).optional(),
    // Values a person gives for parameters selected entries declare, such as which Xero tracking
    // category holds the organisation's projects; an entry given no value keeps the fields that need it, empty.
    parameters: z.array(z.object({
        name: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/u),
        value: z.string().min(1).max(120).regex(/^[^\u0000-\u001f\u007f]+$/u),
    }).strict()).max(16).refine((values) => isSortedUnique(values.map(({ name }) => name)), "Catalogue parameters must be unique and sorted by name.").optional(),
}).strict().refine(({ connector, entries }) => entries.every((entry) => entry.startsWith(`${connector}/`)), { path: ["entries"], message: "Catalogue entries must belong to the selected connector." })
    .refine(({ entries, supersede }) => supersede === undefined || (isSortedUnique(supersede.map(({ entry }) => entry))
    && supersede.every(({ entry }) => entries.includes(entry))), { path: ["supersede"], message: "Superseding entries must be selected entries, unique and sorted." });
export const twinAuthoringPreparationRequestV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.preparation.v1"),
    ...twinAuthoringPreparationRequestFields,
    // A refinement versions through its baseline, so only a full build carries live products.
    liveProducts: twinAuthoringLiveProductsV1Schema.optional(),
    catalogue: twinAuthoringCatalogueSelectionSchema.optional(),
}).strict().refine(warehouseScopeIsSorted, {
    path: ["warehouseOrganisationIds"],
    message: "Warehouse organisation IDs must be unique and sorted.",
});
export const twinAuthoringOperatorGuidanceV1Schema = z.object({
    schemaVersion: z.literal("1.0.0"),
    source: z.literal("operator_prompt"),
    prompt: boundedText(12_000),
    promptSha256: sha256Schema,
}).strict().superRefine((guidance, context) => {
    if (bytesToHex(sha256(guidance.prompt)) !== guidance.promptSha256) {
        context.addIssue({ code: "custom", path: ["promptSha256"],
            message: "Operator guidance prompt hash does not bind the prompt." });
    }
});
export const twinAuthoringSelectedBaselineViewsSchema = z.array(z.string().regex(/^[a-z][a-z0-9_]{0,126}$/u)).min(1).max(64).refine(isSortedUnique, "Baseline views must be unique and sorted.");
/**
 * An operator's reviewed plan that a refinement's author step returns exactly: no model is called,
 * and admission requires the recorded output to equal it. A catalogue plan is never sent: the build
 * worker assembles it from its own catalogue (twinAuthoringCatalogueSelectionSchema).
 */
const operatorPlanFields = {
    source: z.literal("operator"),
    output: z.record(z.string(), z.unknown()),
};
export const twinAuthoringOperatorPlanV1Schema = z.object({
    schemaVersion: z.literal("1.0.0"),
    ...operatorPlanFields,
    outputSha256: sha256Schema,
}).strict();
/** A reviewed plan's output is at most 512 KiB; a preparation request carrying one, a little more. */
export const TWIN_AUTHORING_OPERATOR_PLAN_MAX_BYTES = 512 * 1024;
export const TWIN_AUTHORING_PREPARATION_MAX_BODY_BYTES = TWIN_AUTHORING_OPERATOR_PLAN_MAX_BYTES + 64 * 1024;
/** The plan as a caller gives it; the server adds its version and binds the output's digest. */
export const twinAuthoringOperatorPlanInputSchema = z.object(operatorPlanFields).strict();
export const twinAuthoringPreparationRefinementV1Schema = z.object({
    schemaVersion: z.literal("1.0.0"),
    baselineReleaseId: z.string().regex(/^ta_rel_[0-9a-f]{32}$/u),
    baselineReleaseSha256: sha256Schema,
    baselineSemanticAuthoringRecordSha256: sha256Schema,
    operatorGuidance: twinAuthoringOperatorGuidanceV1Schema,
    operatorPlan: twinAuthoringOperatorPlanV1Schema.optional(),
}).strict();
export const twinAuthoringPreparationRefinementV2Schema = twinAuthoringPreparationRefinementV1Schema.extend({
    schemaVersion: z.literal("2.0.0"),
    selectedBaselineViewNames: twinAuthoringSelectedBaselineViewsSchema,
});
export const twinAuthoringPreparationRequestV2Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.preparation.v2"),
    ...twinAuthoringPreparationRequestFields,
    refinement: z.discriminatedUnion("schemaVersion", [
        twinAuthoringPreparationRefinementV1Schema, twinAuthoringPreparationRefinementV2Schema,
    ]),
}).strict().refine(warehouseScopeIsSorted, {
    path: ["warehouseOrganisationIds"],
    message: "Warehouse organisation IDs must be unique and sorted.",
});
export const twinAuthoringPreparationRequestSchema = z.discriminatedUnion("schemaVersion", [
    twinAuthoringPreparationRequestV1Schema,
    twinAuthoringPreparationRequestV2Schema,
]);
export const twinAuthoringPreparationStateSchema = z.enum([
    "queued", "running", "succeeded", "failed", "cancelled",
]);
export const twinAuthoringPreparationRecordSchema = z.object({
    id: preparationIdSchema,
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    requestSha256: sha256Schema,
    idempotencyKey: boundedText(200),
    state: twinAuthoringPreparationStateSchema,
    attemptCount: z.number().int().nonnegative().safe(),
    availableAt: positiveTimeSchema,
    leaseOwner: boundedText(200).nullable(),
    leaseExpiresAt: positiveTimeSchema.nullable(),
    lastHeartbeatAt: positiveTimeSchema.nullable(),
    startedAt: positiveTimeSchema.nullable(),
    finishedAt: positiveTimeSchema.nullable(),
    lastErrorCode: boundedText(120).nullable(),
    lastErrorMessage: boundedText(4_000).nullable(),
    runId: twinAuthoringRunRecordSchema.shape.id.nullable(),
    submissionSha256: sha256Schema.nullable(),
    createdAt: positiveTimeSchema,
    updatedAt: positiveTimeSchema,
}).strict().superRefine((preparation, context) => {
    const running = preparation.state === "running";
    const terminal = ["succeeded", "failed", "cancelled"].includes(preparation.state);
    if (running !== (preparation.leaseOwner !== null && preparation.leaseExpiresAt !== null)) {
        context.addIssue({ code: "custom", message: "Only running preparations hold a lease." });
    }
    if (terminal !== (preparation.finishedAt !== null)) {
        context.addIssue({ code: "custom", message: "Only terminal preparations are finished." });
    }
    const succeeded = preparation.state === "succeeded";
    if (succeeded !== (preparation.runId !== null && preparation.submissionSha256 !== null)) {
        context.addIssue({ code: "custom", message: "Only succeeded preparations bind a run and submission." });
    }
});
export const claimedTwinAuthoringPreparationSchema = z.object({
    preparation: twinAuthoringPreparationRecordSchema,
    request: twinAuthoringPreparationRequestSchema,
}).strict();
const preparedArtifactSchema = z.object({
    role: twinAuthoringArtifactRoleSchema,
    kind: twinAuthoringArtifactKindSchema,
    artifactType: z.string().regex(/^[a-z][a-z0-9_]{0,119}$/u),
    artifactSchemaVersion: z.string().regex(/^[1-9][0-9]*\.[0-9]+\.[0-9]+$/u),
    contentType: z.literal("application/json"),
    contentSha256: sha256Schema,
    byteSize: z.number().int().positive().max(16 * 1_024 * 1_024).safe(),
    rowCount: z.literal(0),
    contentBase64: z.string().min(1).max(22_369_624)
        .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u),
}).strict();
const requiredPreparedArtifactInventoryV3 = [
    "decision_policy:automated_decision_policy:twin_authoring_automated_decision_policy:1.0.0",
    "discovery:discovery_profile:twin_authoring_discovery:1.0.0",
    "input:input_manifest:twin_authoring_input_manifest:3.0.0",
    "input:source_snapshot:twin_authoring_inspection:2.0.0",
];
const requiredPreparedArtifactInventoryV4 = requiredPreparedArtifactInventoryV3.map((value) => value === "input:input_manifest:twin_authoring_input_manifest:3.0.0"
    ? "input:input_manifest:twin_authoring_input_manifest:4.0.0"
    : value);
export const twinAuthoringPreparationResultV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.preparation-result.v1"),
    preparationId: preparationIdSchema,
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    idempotencyKey: boundedText(200),
    producer: z.object({
        name: boundedText(120),
        version: boundedText(80),
        buildSha256: sha256Schema,
    }).strict(),
    initialArtifacts: z.array(preparedArtifactSchema).length(4),
    runRequest: twinAuthoringRunRequestV1Schema,
}).strict().superRefine((result, context) => {
    const inventory = result.initialArtifacts.map((artifact) => `${artifact.role}:${artifact.kind}:${artifact.artifactType}:${artifact.artifactSchemaVersion}`).sort();
    if (![requiredPreparedArtifactInventoryV3, requiredPreparedArtifactInventoryV4].some((expected) => JSON.stringify(inventory) === JSON.stringify(expected))) {
        context.addIssue({
            code: "custom",
            path: ["initialArtifacts"],
            message: "Prepared inputs must contain the exact four V3 or V4 bootstrap artifacts.",
        });
    }
    const run = result.runRequest;
    if (run.tenantSlug !== result.tenantSlug || run.organisationId !== result.organisationId
        || run.idempotencyKey !== result.idempotencyKey || run.productScope.mode !== "all") {
        context.addIssue({
            code: "custom",
            path: ["runRequest"],
            message: "Prepared run authority must match its preparation and author all products.",
        });
    }
});
export const twinAuthoringPreparationEventKindSchema = z.enum([
    "preparation_queued",
    "preparation_claimed",
    "preparation_requeued",
    "preparation_cancelled",
    "preparation_succeeded",
    "preparation_failed",
]);
export const twinAuthoringPreparationEventV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.preparation-event.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    preparationId: preparationIdSchema,
    kind: twinAuthoringPreparationEventKindSchema,
    actor: boundedText(200),
    attributes: z.record(z.string().regex(/^[a-z][a-zA-Z0-9]{0,79}$/u), z.union([
        z.string().max(1_000), z.number().finite().safe(), z.boolean(), z.null(),
    ])).refine((attributes) => Object.keys(attributes).length <= 32),
    createdAt: positiveTimeSchema,
}).strict();
export function canonicalTwinAuthoringPreparationRequestJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringPreparationRequestSchema.parse(input));
}
export function canonicalTwinAuthoringPreparationResultJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringPreparationResultV1Schema.parse(input));
}
export function canonicalTwinAuthoringPreparationEventJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringPreparationEventV1Schema.parse(input));
}
